import { ChevronLeft, CreditCard, Eye, EyeOff, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SpaceEditHeaderProps {
  space: any;
  isDirty: boolean;
}

export function SpaceEditHeader({ space, isDirty }: SpaceEditHeaderProps) {
  const navigate = useNavigate();

  return (
    <div
      style={{
        background: 'white',
        borderBottom: '2px solid #f0f0f0',
        padding: '1.5rem 2rem',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ width: '100%' }}>
            <button
              onClick={() => navigate('/spaces')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.4rem 0.8rem',
                background: 'transparent',
                color: '#00758d',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
                marginBottom: '1rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#00758d';
                e.currentTarget.style.background = '#f8fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <ChevronLeft size={16} />
              Retour
            </button>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                width: '100%',
                gap: '1.5rem',
              }}
            >
              <div style={{ display: 'flex', gap: '2rem', flex: 1, alignItems: 'center' }}>
                <div
                  style={{
                    width: '140px',
                    height: '140px',
                    flexShrink: 0,
                    borderRadius: '20px',
                    background: space.imageUrl
                      ? `url(${space.imageUrl}) center/cover`
                      : `linear-gradient(135deg, ${space.colorPrimary || '#00758d'} 0%, ${space.colorAccent || '#8E0B56'} 100%)`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    border: '4px solid white',
                  }}
                />

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
                    <h1
                      style={{
                        fontSize: '2rem',
                        fontWeight: 800,
                        color: '#00303c',
                        margin: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      {space.name}
                    </h1>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          background: space.isActive ? 'rgba(0,117,141,0.1)' : 'rgba(142,11,86,0.1)',
                          color: space.isActive ? '#00758d' : '#8E0B56',
                          border: `1px solid ${space.isActive ? 'rgba(0,117,141,0.2)' : 'rgba(142,11,86,0.2)'}`,
                        }}
                      >
                        {space.isActive ? 'Actif' : 'Inactif'}
                      </span>

                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          background: space.isPublic ? 'rgba(0,48,60,0.1)' : '#f3f4f6',
                          color: space.isPublic ? '#00303c' : '#6b7280',
                          border: `1px solid ${space.isPublic ? 'rgba(0,48,60,0.2)' : '#e5e7eb'}`,
                        }}
                      >
                        {space.isPublic ? <Eye size={12} /> : <EyeOff size={12} />}
                        {space.isPublic ? 'Public' : 'Brouillon'}
                      </span>
                    </div>
                  </div>

                  <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#666',
                      margin: '0.5rem 0',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {space.description || space.shortDesc || "Aucune description de l'espace pour le moment."}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginTop: '0.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.85rem',
                        color: '#666',
                        fontFamily: 'monospace',
                        background: '#f3f4f6',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '4px',
                      }}
                    >
                      /{space.slug}
                    </div>
                    {space.category && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#00758d',
                        }}
                      >
                        <LayoutDashboard size={14} />
                        {space.category}
                      </div>
                    )}
                    {space.workflow && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#dfc32f',
                        }}
                      >
                        <CreditCard size={14} />
                        {space.workflow === 'IMMEDIATE'
                          ? 'Réservation immédiate'
                          : space.workflow === 'QUOTE_REQUIRED'
                            ? 'Devis requis'
                            : 'Candidature requise'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isDirty && (
                <div
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#b45309',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 2px 8px rgba(217,119,6,0.05)',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#d97706',
                      animation: 'pulse 2s infinite',
                    }}
                  ></div>
                  Modifications en cours...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(217, 119, 6, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
        }
      `}</style>
    </div>
  );
}
